import {HttpClient} from "@angular/common/http";
import {Inject, Injectable} from "@angular/core";
import {firstValueFrom} from "rxjs";
import {
  SOCIAL_OAUTH_PENDING_PROVIDER_STORAGE_KEY,
  SOCIAL_OAUTH_STATE_STORAGE_PREFIX
} from "../../constants/sso.constants";
import {PostLoginRedirectService} from "./post-login-redirect.service";

interface SatellizerOAuth2ProviderConfig {
  name: string;
  url: string;
  authorizationEndpoint: string;
  clientId: string;
  redirectUri: string;
  scope: string[];
  scopePrefix?: string;
  scopeDelimiter: string;
  requiredUrlParams?: string[];
  optionalUrlParams?: string[];
}

// These mirror OAuth2.prototype.defaults inside satellizer itself
// (node_modules/satellizer/dist/satellizer.js) - they're not part of the per-provider
// config set via $authProvider.google()/.oauth2() in authentication.config.ts, so they
// have to be reproduced here to rebuild the same authorize URL without going through
// satellizer's popup-only OAuth2.prototype.init().
const DEFAULT_URL_PARAMS = ['response_type', 'client_id', 'redirect_uri'];
const RESPONSE_TYPE = 'code';

@Injectable({providedIn: 'root'})
export class SocialOauthRedirectService {

  constructor(@Inject('SatellizerConfig') private satellizerConfig: { providers: { [name: string]: SatellizerOAuth2ProviderConfig } },
              @Inject(PostLoginRedirectService) private postLoginRedirectService: PostLoginRedirectService,
              @Inject(HttpClient) private http: HttpClient) {
  }

  // Starts login for a satellizer-configured OAuth2 provider (google, microsoft) via a
  // full-page redirect instead of satellizer's popup flow. The popup flow polls
  // popup.closed to detect completion, which browsers' Cross-Origin-Opener-Policy
  // restrictions on the IdP's login page can silently block - the same issue the Auth0
  // SSO login had.
  login(providerName: string): void {
    const provider = this.satellizerConfig.providers[providerName];
    if (!provider) {
      throw new Error(`Unknown social login provider: ${providerName}`);
    }

    localStorage.removeItem('admin_token');
    localStorage.removeItem('access_token');

    this.postLoginRedirectService.capturePreviousState();
    sessionStorage.setItem(SOCIAL_OAUTH_PENDING_PROVIDER_STORAGE_KEY, providerName);

    window.location.href = `${provider.authorizationEndpoint}?${this.buildQueryString(provider)}`;
  }

  // Call on every app boot. Resolves once any pending callback has been handled (or
  // immediately if there is none). A successful exchange navigates away, so callers
  // don't need to do anything further with the result.
  async tryHandleCallback(): Promise<void> {
    const providerName = sessionStorage.getItem(SOCIAL_OAUTH_PENDING_PROVIDER_STORAGE_KEY);
    if (!providerName) {
      return;
    }
    sessionStorage.removeItem(SOCIAL_OAUTH_PENDING_PROVIDER_STORAGE_KEY);

    const rawSearch = (window as any).__initialLocationSearch ?? window.location.search;
    const params = new URLSearchParams(rawSearch);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    const stateStorageKey = SOCIAL_OAUTH_STATE_STORAGE_PREFIX + providerName;
    const savedState = sessionStorage.getItem(stateStorageKey);
    sessionStorage.removeItem(stateStorageKey);

    if (error || !code) {
      console.error('[Social login] provider returned no authorization code', providerName, error);
      this.postLoginRedirectService.navigateToLoginWithError();
      return;
    }
    if (!savedState || state !== savedState) {
      console.error('[Social login] state missing or mismatched, aborting exchange', providerName);
      this.postLoginRedirectService.navigateToLoginWithError();
      return;
    }

    const provider = this.satellizerConfig.providers[providerName];
    const payload: { code: string, clientId: string, redirectUri: string, state?: string } = {
      code,
      clientId: provider.clientId,
      redirectUri: provider.redirectUri,
    };
    if (state) {
      payload.state = state;
    }

    // provider.url (from satellizer's config) is already an absolute URL
    // (apiConfig.baseUrl + '/iam/authenticate/<name>') - AuthInterceptor prepends its
    // own baseUrl to every request unconditionally, so passing it through as-is would
    // double the host. Use the relative path and let the interceptor add the base URL.
    const exchangeUrl = `/iam/authenticate/${providerName}`;

    let response: any;
    try {
      response = await firstValueFrom(this.http.post(exchangeUrl, payload));
    } catch (err) {
      console.error('[Social login] token exchange failed', providerName, err);
      this.postLoginRedirectService.navigateToLoginWithError();
      return;
    }

    const token = response?.access_token || response?.token;
    if (!token) {
      console.error('[Social login] exchange response carried no token', providerName, response);
      this.postLoginRedirectService.navigateToLoginWithError();
      return;
    }
    localStorage.setItem('admin_token', token);

    this.postLoginRedirectService.navigateToPostLoginUrl();
  }

  // Mirrors OAuth2.prototype.buildQueryString in satellizer.
  private buildQueryString(provider: SatellizerOAuth2ProviderConfig): string {
    const paramCategories = [DEFAULT_URL_PARAMS, provider.requiredUrlParams || [], provider.optionalUrlParams || []];
    const pairs: string[] = [];

    for (const category of paramCategories) {
      for (const paramName of category) {
        // 'display' (e.g. display=popup) is a UI hint for the popup flow only - carrying
        // it into a full-page redirect makes some providers render a stripped-down page.
        // 'state' is handled separately below: it's always generated here, rather than
        // left to each provider's config, which is what satellizer itself does - and
        // which silently drops CSRF state validation entirely for any provider (e.g.
        // Microsoft, in this app's config) that doesn't explicitly opt into a state.
        if (paramName === 'display' || paramName === 'state') {
          continue;
        }

        let value: any = paramName === 'response_type' ? RESPONSE_TYPE : (provider as any)[this.camelCase(paramName)];

        if (paramName === 'redirect_uri' && !value) {
          continue;
        }
        if (paramName === 'scope' && Array.isArray(value)) {
          value = value.join(provider.scopeDelimiter);
          if (provider.scopePrefix) {
            value = [provider.scopePrefix, value].join(provider.scopeDelimiter);
          }
        }
        if (value === undefined || value === null) {
          continue;
        }
        pairs.push(`${paramName}=${value}`);
      }
    }

    const state = this.generateState();
    sessionStorage.setItem(SOCIAL_OAUTH_STATE_STORAGE_PREFIX + provider.name, state);
    pairs.push(`state=${encodeURIComponent(state)}`);

    return pairs.join('&');
  }

  private camelCase(name: string): string {
    return name.replace(/([:\-_]+(.))/g, (_match, _sep, letter, offset) => offset ? letter.toUpperCase() : letter);
  }

  private generateState(): string {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
}
